import {
  ArchiveBoxIcon,
  BellAlertIcon,
  BookOpenIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { Sale, SaleSearch } from '@qogita/canary-types';
import { DeprecatedCombobox } from '@qogita/ui';
import React from 'react';

import { LoadingDots } from '#components/shared/LoadingDots';
import { SearchState } from '#hooks/api/useSearch';
import { routes } from '#utils/routes';

import { SearchBarHit } from './SearchBarHit';

// Each key represents a section of the search results.
export type SearchOrderConfig = {
  currentOrdersIndex: number;
  completedOrdersIndex: number;
  expiredOrdersIndex: number;
  inventoryIndex: number;
};

export const getMappedTabForStatus = (status: Sale['status']) => {
  switch (status) {
    case 'CLOSED':
    case 'COMPLETED':
    case 'PAID':
      return 'COMPLETED' as const;
    case 'NOTIFIED':
    case 'OPEN':
      return 'CURRENT' as const;
    case 'DECLINED':
    case 'EXPIRED':
      return 'EXPIRED' as const;
  }
};

const sortSalesByGroup = (sales: SaleSearch[], indexes: SearchOrderConfig) => {
  const GroupIndex = {
    CURRENT: indexes.currentOrdersIndex,
    COMPLETED: indexes.completedOrdersIndex,
    EXPIRED: indexes.expiredOrdersIndex,
  } as const;

  return sales.sort(
    (previous, next) =>
      GroupIndex[getMappedTabForStatus(previous.status)] -
      GroupIndex[getMappedTabForStatus(next.status)],
  );
};

const searchItemHeaderProps = {
  CURRENT: {
    headerIcon: BellAlertIcon,
    headerTitle: 'Current Orders',
  },
  COMPLETED: {
    headerIcon: ClipboardDocumentListIcon,
    headerTitle: 'Completed Orders',
  },
  EXPIRED: {
    headerIcon: ArchiveBoxIcon,
    headerTitle: 'Expired Orders',
  },
  INVENTORY: {
    headerIcon: BookOpenIcon,
    headerTitle: 'Inventory',
  },
} as const;

type SearchBarHitsProps = SearchState &
  SearchOrderConfig & {
    query: string;
  };

function getHeaderProps(
  previousSale: SaleSearch | undefined,
  sale: SaleSearch,
  itemIndex: number,
) {
  const currentSaleTab = getMappedTabForStatus(sale.status);
  // Show header if is first item
  if (itemIndex === 0) {
    return searchItemHeaderProps[currentSaleTab];
  }

  // This covers some type possibilities where the previousSale is undefined.
  if (!previousSale) {
    return undefined;
  }

  // Show header if the tab for the current sale is different from the previous sale
  if (currentSaleTab !== getMappedTabForStatus(previousSale.status)) {
    return searchItemHeaderProps[currentSaleTab];
  }

  // Otherwise, don't show header
  return undefined;
}

export const SearchBarHits = ({
  isSearching,
  sales,
  offers,
  query,
  currentOrdersIndex,
  completedOrdersIndex,
  expiredOrdersIndex,
  inventoryIndex,
}: SearchBarHitsProps) => {
  const isEmptyResults =
    !isSearching && sales?.length === 0 && offers?.length === 0;

  const offersLength = offers.length;
  const salesLength = sales.length;

  const inventoryResults = offers
    ? offers.map((offer, itemIndex) => {
        const href = routes.inventorySingular(offer.qid);
        // compute index considering section ordering
        const index =
          inventoryIndex === 0 ? itemIndex : itemIndex + salesLength;

        // show header if is first item
        const displayHeader = itemIndex === 0;

        const headerIconAndTitle =
          displayHeader && searchItemHeaderProps['INVENTORY'];

        return (
          <SearchBarHit
            {...headerIconAndTitle}
            key={offer.qid}
            objectID={offer.qid}
            title={offer.variantGtin}
            description={offer.variantName}
            index={index}
            query={query}
            href={href}
            item={{ ...offer, href }}
          />
        );
      })
    : null;

  const salesGroupIndexes = {
    currentOrdersIndex,
    completedOrdersIndex,
    expiredOrdersIndex,
    inventoryIndex,
  };

  const saleResults = sales
    ? sortSalesByGroup(sales, salesGroupIndexes).map(
        (sale, itemIndex, sales) => {
          const href = routes.order(sale.qid);

          const previousSale = sales?.[itemIndex - 1];
          const headerProps = getHeaderProps(previousSale, sale, itemIndex);

          // Compute index considering section ordering
          const isInventoryTab = inventoryIndex === 0;
          const index = itemIndex + (isInventoryTab ? offersLength : 0);
          return (
            <SearchBarHit
              {...headerProps}
              key={sale.qid}
              objectID={sale.qid}
              title={sale.orderFid}
              description={
                query
                  ? sale.shipmentsCodes?.find((code) => code.includes(query))
                  : undefined
              }
              index={index}
              query={query}
              href={href}
              item={{ ...sale, href }}
            />
          );
        },
      )
    : null;

  return (
    <DeprecatedCombobox.Options className="ring-1 ring-gray-300">
      {isSearching ? (
        <li className="flex cursor-not-allowed gap-3 px-4 py-2 text-sm text-white">
          <LoadingDots />
        </li>
      ) : null}

      {inventoryIndex === 0 ? (
        <>
          {inventoryResults}
          {saleResults}
        </>
      ) : (
        <>
          {saleResults}
          {inventoryResults}
        </>
      )}

      {isEmptyResults ? (
        <li
          className={'flex cursor-not-allowed gap-3 px-4 py-2 text-base'}
          key="No match"
        >
          No matches found
        </li>
      ) : null}
    </DeprecatedCombobox.Options>
  );
};
